// React
import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

// Components
import {
  PrimaryButton,
  DialogBox
} from '../../msreact-kit/packages/html-components';
import Footer from '../BaseLayout/footer';
import DiscountGrid from './discountGrid';
import Loader from '../Shared/Utils/Loader';
import CustomizedSnackbars from '../Shared/Utils/SnackBar';

// Actions
import {
  getCouponsDetails,
  deleteCoupon
} from '../../actions/discount-service-action';
import { setSnackbar } from '../../actions/generic-action';

// Constants
import { COUPON_MODE } from './coupon-details/data';
import { TABS } from './data';

// Css
import './index.scss';

const TAB_STATUS_MAP = {
  all: null,
  activated: 'ACTIVE',
  deactivated: 'INACTIVE',
  used: 'COMPLETED',
  expired: 'EXPIRED'
};

const DiscountManagement = memo((props) => {
  const dispatch = useDispatch();
  const { content, snackbarStatus } = useSelector((state) => ({
    content: state.ContentReducer.content,
    snackbarStatus: state.GenericReducer.snackbarStatus
  }));
  const discountContent = content?.components?.discount_management;

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const processedRef = useRef(false);

  /* ── Load coupons from API ── */
  const loadCoupons = useCallback(
    async (tab, page, size) => {
      setLoading(true);
      const status = TAB_STATUS_MAP[tab] || null;
      const result = await dispatch(getCouponsDetails({ status, page, size }));
      if (result) {
        setCoupons(result.content ?? []);
        setTotalPages(result.totalPages ?? 0);
      }
      setLoading(false);
    },
    [dispatch]
  );

  /* ── Initial load + re-fetch on tab / page / size change ── */
  useEffect(() => {
    loadCoupons(activeTab, currentPage, pageSize);
  }, [activeTab, currentPage, pageSize, loadCoupons]);

  /* ── Handle data returned from CouponDetails page ── */
  useEffect(() => {
    const state = props.location?.state;
    if (!state || processedRef.current) return;
    processedRef.current = true;
    /* After save/delete, re-fetch the current page to reflect server state */
    loadCoupons(activeTab, currentPage, pageSize);
    /* Show success snackbar if a message was passed */
    if (state.successMessage) {
      dispatch(
        setSnackbar({
          showSnackbar: true,
          message: state.successMessage,
          severity: 'success'
        })
      );
    }
    /* Clear state so back navigation does not re-apply */
    props.history.replace('/dashboard/discount-management', {});
  }, []);

  /* ── Table action handler ── */
  const handleAction = (type, row) => {
    if (type === 'delete') {
      setPendingDelete(row);
      return;
    }
    props.history.push({
      pathname: '/dashboard/discount-management/coupon',
      state: { coupon: row },
      search: `?mode=${type}`
    });
  };

  /* ── Delete from listing ── */
  const handleDeleteConfirm = async () => {
    const couponId = pendingDelete?.id;
    setPendingDelete(null);
    if (couponId) {
      const result = await dispatch(deleteCoupon(couponId));
      if (result) {
        dispatch(
          setSnackbar({
            showSnackbar: true,
            message: discountContent?.coupon_details?.messages?.coupon_deleted,
            severity: 'success'
          })
        );
        await loadCoupons(activeTab, currentPage, pageSize);
      }
    }
  };

  /* ── Create button ── */
  const handleCreateCoupon = () => {
    props.history.push({
      pathname: '/dashboard/discount-management/coupon',
      search: `?mode=${COUPON_MODE.add}`
    });
  };

  /* ── Tab filtering ── */
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setCurrentPage(0);
  };

  /* ── Page / size change from DiscountGrid ── */
  const handlePageChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(0);
    } else {
      setCurrentPage(page);
    }
  };

  return (
    <div>
      {loading && <Loader />}

      {snackbarStatus?.showSnackbar && (
        <CustomizedSnackbars
          severity={snackbarStatus?.severity}
          message={snackbarStatus?.message}
          changeState={() =>
            dispatch(setSnackbar({ showSnackbar: false, message: '' }))
          }
        />
      )}

      {pendingDelete && (
        <DialogBox
          title={discountContent?.delete_dialog?.title}
          subTitle={`${discountContent?.delete_dialog?.subtitle_prefix} "${pendingDelete.code}" ${discountContent?.delete_dialog?.subtitle_suffix}`}
          isModal
          isSuccess={false}
          handleConfirm={handleDeleteConfirm}
          handleCancel={() => setPendingDelete(null)}
        />
      )}

      <div className='discount-management__container'>
        <div className='discount-management__container__title-wrapper'>
          <div className='discount-management__container__title'>
            <h5 className='discount-management__container__header -size-l'>
              {discountContent?.page?.title}
            </h5>
            <div className='discount-management__container__text'>
              <p>{discountContent?.page?.subtitle}</p>
            </div>
          </div>

          <div className='discount-management__grid'>
            <PrimaryButton
              id='createCoupon'
              label=''
              onClick={handleCreateCoupon}
              className='discount-management__add-button'
            >
              <span className='a-button__label'>
                <i className='a-icon boschicon-bosch-ic-add' />
                <span className='d-sm-none'>
                  {discountContent?.page?.create_button}
                </span>
              </span>
            </PrimaryButton>
          </div>
        </div>

        <div className='discount-management__tabs' role='tablist'>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type='button'
              role='tab'
              aria-selected={activeTab === tab.key}
              className={`discount-management__tabs__item${activeTab === tab.key ? ' discount-management__tabs__item--active' : ''}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {discountContent?.tabs?.[tab.key]}
            </button>
          ))}
        </div>

        <div className='discount-management__table'>
          <DiscountGrid
            data={coupons}
            onAction={handleAction}
            tableContent={discountContent?.table}
            statusLabels={discountContent?.coupon_details?.status_labels}
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <Footer customClassName='d-lg-none' />
    </div>
  );
});

DiscountManagement.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};

export default DiscountManagement;
